import { fetch, handleResponse } from "js/lib/server.js";

const LEVEL_PLACE = 4;
const LEVEL_CITY = 3;
const LEVEL_REGION = 2;
const LEVEL_COUNTRY = 1;

const URL = "/api/geo";

const ICON_MAP = {
    administrative: "map",
    boundary: "map",
    route: "map",
    locality: "location_city",
    archaeological_site: "attractions",
    ruins: "castle",
    monument: "castle",
    attraction: "museum",
    wood: "nature",
    river: "nature",
    dock: "water",
    park: "park",
    valley: "landscape",
    viewpoint: "landscape",
    hamlet: "agriculture",
    farm: "agriculture",
    restaurant: "restaurant",
    waterway: "water",
    water: "water",
    building: "architecture",
    street: "signpost",
};

class Geo {
    constructor(params) {
        this.name = params.name ?? this.prepareName(params);
        this.lat = params.lat;
        this.long = params.long;
        this.city = params.city;
        this.region = params.region;
        this.region_code = params.region_code;
        this.country = params.country;
        this.country_code = (params.country_code ?? "").toUpperCase();
        this.api = params.api;
        this.wiki = params.wiki;
        this.level = params.level;
        this.ui_name = params.ui_name;
        this.ui_type = params.ui_type;
        this.applyFixes();
    }

    applyFixes() {
        if (this.country_code === "VA" && this.level == LEVEL_CITY) {
            this.city = this.name;
        }
    }

    prepareName(params) {
        if (params.city) {
            return params.city;
        }
        if (params.region) {
            return `${params.country} / ${params.region}`;
        }
        return params.country;
    }

    toJson() {
        return {
            name: this.name,
            city: this.city,
            region: this.region,
            region_code: this.region_code,
            country: this.country,
            country_code: this.country_code,
            lat: this.lat,
            long: this.long,
            api: this.api,
            wiki: this.wiki,
            level: this.level,
        };
    }

    getCoordinates() {
        return [parseFloat(this.long), parseFloat(this.lat)];
    }

    getFlag() {
        return [...this.country_code]
            .map((letter) =>
                String.fromCodePoint(127397 + letter.charCodeAt(0))
            )
            .join("");
    }

    setValue(value) {
        this.value = value;
        return this;
    }

    setMeta(meta) {
        this.meta = meta;
        return this;
    }

    getValue() {
        return this.value;
    }

    getMeta() {
        return this.meta;
    }
}

function fromApi(raw) {
    return new Geo({
        name: raw.name,
        lat: raw.lat,
        long: raw.long,
        level: raw.level,
        city: raw.city,
        region: raw.region,
        region_code: raw.region_code,
        country: raw.country,
        country_code: raw.country_code,
        api: raw.api,
        wiki: raw.wiki,
        ui_name: raw.label,
        ui_type: raw.category,
    });
}

async function getGeoFromLatLong(lat, lon) {
    return fetch(`${URL}/reverse`, { params: { lat, lon } })
        .then(handleResponse)
        .then(fromApi);
}

async function getGeos(name, level) {
    return fetch(`${URL}/search`, { params: { q: name, level } })
        .then(handleResponse)
        .then((list) => list.map(fromApi));
}

async function getPlace(place, country) {
    const geos = await getGeos(
        country ? `${place}, ${country}` : place,
        LEVEL_PLACE
    );
    return (geos ?? []).length > 0 ? geos[0] : null;
}
async function getCity(place, country) {
    const geos = await getGeos(place, LEVEL_CITY);
    if (geos.length > 0) {
        return geos[0];
    }
    const fallback = await getPlace(place, country);
    if (fallback) {
        return new Geo({ ...fallback, level: LEVEL_CITY });
    }
    return null;
}
async function getRegion(place, country) {
    const geos = await getGeos(place, LEVEL_REGION);
    if (geos.length > 0) {
        return geos[0];
    }
    const fallback = await getPlace(place, country);
    if (fallback) {
        return new Geo({ ...fallback, level: LEVEL_REGION });
    }
    return null;
}
async function getCountry(country) {
    let geos = await getGeos(country, LEVEL_COUNTRY);
    if (geos.length > 0) {
        return geos[0];
    }
    geos = await getGeos(country, LEVEL_PLACE);
    if (geos.length > 0) {
        return new Geo({ ...geos[0], level: LEVEL_COUNTRY });
    }
    return null;
}

export {
    Geo,
    getGeos,
    getGeoFromLatLong,
    getPlace,
    getCity,
    getRegion,
    getCountry,
    ICON_MAP,
};
